import { useEffect, useState, useRef } from "react";
import styles from "./Workshop.module.css";
import { TextField, CircularProgress } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ImageIcon from "@mui/icons-material/Image";
import validator from "validator";
import api from "../../services/api";
import loadingImage from "../loading.gif";
import mainStyles from "../Main.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addWorkshop } from "../../State/Workshop/Action";

export default function Workshop() {
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);

  //States for form fields
  const [name, setName] = useState("");
  const [ownerName, setOwnerName] = useState();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [street, setStreet] = useState("");
  const [shopNumber, setShopNumber] = useState("");
  const [address, setAddress] = useState("");

  //Validators state
  const [isValidPhone, setIsValidPhone] = useState(true);
  const [hasError, setHasError] = useState(false);

  const [workshopId, setWorkshopId] = useState("");
  const [image1, setImage1] = useState();
  const [image2, setImage2] = useState();
  const [image3, setImage3] = useState();
  const [image4, setImage4] = useState();
  const [image5, setImage5] = useState();

  const [image1Url, setImage1Url] = useState();
  const [image2Url, setImage2Url] = useState();
  const [image3Url, setImage3Url] = useState();
  const [image4Url, setImage4Url] = useState();
  const [image5Url, setImage5Url] = useState();

  const [image1Uploaded, setImage1Uploaded] = useState(false);
  const [image2Uploaded, setImage2Uploaded] = useState(false);
  const [image3Uploaded, setImage3Uploaded] = useState(false);
  const [image4Uploaded, setImage4Uploaded] = useState(false);
  const [image5Uploaded, setImage5Uploaded] = useState(false);

  const workshopResponse = useSelector(
    (store) => store.workshopResponse.workshop
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [image1UploadFail, setImage1UploadFail] = useState(false);
  const [image2UploadFail, setImage2UploadFail] = useState(false);
  const [image3UploadFail, setImage3UploadFail] = useState(false);
  const [image4UploadFail, setImage4UploadFail] = useState(false);
  const [image5UploadFail, setImage5UploadFail] = useState(false);

  const [image1Loading, setImage1Loading] = useState(false);
  const [image2Loading, setImage2Loading] = useState(false);
  const [image3Loading, setImage3Loading] = useState(false);
  const [image4Loading, setImage4Loading] = useState(false);
  const [image5Loading, setImage5Loading] = useState(false);

  useEffect(() => {
    // Only process response if we're currently submitting
    if (isSubmittingRef.current) {
      if (workshopResponse && Object.keys(workshopResponse).length > 0) {
        // Success: we got a response while submitting
        onSuccessfulSave();
        isSubmittingRef.current = false;
      }
    }
  }, [workshopResponse]);

  const updateImage = (res) => {
    if (res.imageNumber === "IMAGE1") {
      setImage1Url(res.imageUrl);
    } else if (res.imageNumber === "IMAGE2") {
      setImage2Url(res.imageUrl);
    } else if (res.imageNumber === "IMAGE3") {
      setImage3Url(res.imageUrl);
    } else if (res.imageNumber === "IMAGE4") {
      setImage4Url(res.imageUrl);
    } else if (res.imageNumber === "IMAGE5") {
      setImage5Url(res.imageUrl);
    }
  };

  // Phone Number Validation
  const validatePhone = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    const limitedValue = value.slice(0, 10); // Limit to 10 digits
    setPhoneNumber(limitedValue);
    // Set validation state based on the new value
    setIsValidPhone(limitedValue.length === 10);
  };

  const isValidForm = () => {
    const trimmedName = name?.trim();
    const trimmedOwnerName = ownerName?.trim();
    
    // Only workshop name and owner name are mandatory
    const hasRequiredFields = trimmedName && trimmedOwnerName;
    
    return hasRequiredFields;
  };

  const onSuccessfulSave = () => {
    setIsSaved(true);
    setIsLoading(false);
    setHasError(false);
    // Redirect to show-my-workshop page after successful save
    setTimeout(() => {
      navigate("/show-my-workshop");
    }, 1000);
  };

  const onFailedSave = () => {
    setIsLoading(false);
    setIsSaved(false);
    setHasError(true);
  };

  const resetForm = () => {
    setName("");
    setOwnerName("");
    setPhoneNumber("");
    setStreet("");
    setShopNumber("");
    setAddress("");
    setWorkshopId("");
    setImage1Url(null);
    setImage2Url(null);
    setImage3Url(null);
    setImage4Url(null);
    setImage5Url(null);
    setImage1Uploaded(false);
    setImage2Uploaded(false);
    setImage3Uploaded(false);
    setImage4Uploaded(false);
    setImage5Uploaded(false);
    setImage1UploadFail(false);
    setImage2UploadFail(false);
    setImage3UploadFail(false);
    setImage4UploadFail(false);
    setImage5UploadFail(false);
    setImage1Loading(false);
    setImage2Loading(false);
    setImage3Loading(false);
    setImage4Loading(false);
    setImage5Loading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setIsSaved(false);
    setHasError(false);
    isSubmittingRef.current = true;
    const workshop = {
      workshopId,
      name,
      ownerName,
      shopNumber,
      phoneNumber,
      street,
      address,
    };

    const formIsValid = isValidForm();
    if (!formIsValid) {
      console.log("Form validation failed:", {
        name: name?.trim(),
        ownerName: ownerName?.trim()
      });
      isSubmittingRef.current = false;
      onFailedSave();
      return;
    }

    dispatch(addWorkshop(workshop));
  };

  return isLoading ? (
    <div className={mainStyles.loading}>
      <img src={loadingImage} alt="loading" />
    </div>
  ) : (
    <div>
      <header className={mainStyles.header}>Add Workshop</header>
      <div className="max-w-lg mx-auto p-6 bg-white shadow-lg rounded-lg space-y-4">
        <TextField
          fullWidth
          variant="outlined"
          className="border-gray-300 rounded-md"
          onChange={(e) => setName(e.target.value)}
          label="Enter Workshop/Garage Name"
        />
        <TextField
          fullWidth
          variant="outlined"
          className="border-gray-300 rounded-md"
          onChange={(e) => setOwnerName(e.target.value)}
          label="Enter Owner Name"
        />
        <TextField
          fullWidth
          variant="outlined"
          className="border-gray-300 rounded-md"
          type="tel"
          onChange={validatePhone}
          value={phoneNumber}
          inputProps={{ maxLength: 10 }}
          label="Enter Phone Number"
        />
        {!isValidPhone && phoneNumber.length > 0 && (
          <p className="text-red-500 text-sm">
            Please enter a valid 10-digit Phone number
          </p>
        )}
        <TextField
          fullWidth
          variant="outlined"
          className="border-gray-300 rounded-md"
          multiline
          rows={4}
          onChange={(e) => setAddress(e.target.value)}
          label="Enter Address"
        />
      </div>

      {[1, 2, 3, 4, 5].map((num) => {
        const imageUrl = eval(`image${num}Url`);
        const imageUploaded = eval(`image${num}Uploaded`);
        const imageUploadFail = eval(`image${num}UploadFail`);
        const imageLoading = eval(`image${num}Loading`);
        
        const handleFileChange = (e, num) => {
          const file = e.target.files[0];
          if (file) {
            eval(`setImage${num}(file)`);
            eval(`setImage${num}Loading(true)`);
            eval(`setImage${num}Uploaded(false)`);
            eval(`setImage${num}UploadFail(false)`);
            
            // Automatically upload when file is selected
            const formData = new FormData();
            formData.append("file", file);
            formData.append("imageNumber", `IMAGE${num}`);
            if (workshopId) {
              formData.append("workshopId", workshopId);
            } else {
              formData.append("workshopId", "");
            }
            
            api
              .post("/workshops/images", formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              })
              .then((res) => {
                setWorkshopId(res.data.workshopId);
                eval(`setImage${num}Loading(false)`);
                eval(`setImage${num}Uploaded(true)`);
                eval(`setImage${num}UploadFail(false)`);
                updateImage(res.data);
              })
              .catch((error) => {
                console.log(error);
                eval(`setImage${num}Loading(false)`);
                eval(`setImage${num}Uploaded(false)`);
                eval(`setImage${num}UploadFail(true)`);
              });
          }
        };
        
        return (
          <div key={num} className="mb-6">
            <div className="text-lg text-center font-semibold text-gray-700 mb-2">
              Image {num}
            </div>
            <div className="flex flex-col items-center gap-4 p-4 border rounded-xl shadow-md bg-white">
              {imageUrl && (
                <div className="w-full max-w-md mb-4">
                  <img
                    src={imageUrl}
                    alt={`image${num}`}
                    className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 shadow-md"
                  />
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                className="hidden"
                id={`fileInput${num}`}
                onChange={(e) => handleFileChange(e, num)}
                disabled={imageLoading}
              />
              
              <label
                htmlFor={`fileInput${num}`}
                className={`w-full py-3 px-6 font-semibold rounded-lg shadow-md transition flex items-center justify-center gap-2 ${
                  imageLoading
                    ? "bg-blue-400 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                }`}
              >
                {imageLoading ? (
                  <>
                    <CircularProgress size={20} thickness={4} sx={{ color: 'white' }} />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-5 h-5" />
                    <span>{imageUrl ? "Change Image" : "Upload Image"}</span>
                  </>
                )}
              </label>

              {imageUploaded && !imageLoading && (
                <p className="text-green-600 font-medium">
                  Image uploaded successfully
                </p>
              )}
              {imageUploadFail && !imageLoading && (
                <p className="text-red-600 font-medium">Image upload failed</p>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex justify-center">
        <button
          onClick={handleSubmit}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold text-lg rounded-lg shadow-md hover:bg-blue-700 active:bg-blue-800 transition focus:ring-2 focus:ring-blue-400"
        >
          <SaveIcon className="w-6 h-6" />
          Save Workshop
        </button>
      </div>

      <div className={styles.componentContainer}>
        {hasError && (
          <p className={mainStyles.error}>
            Please enter all the mandatory fields with correct value
          </p>
        )}

        {isSaved && (
          <div>
            <p className={mainStyles.success}>Workshop Saved Successfully</p>
          </div>
        )}
      </div>
    </div>
  );
}
